import type { Counterparty } from "@station/contracts";
import {
  Alert,
  Button,
  EmptyState,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { HandCoins, Landmark, Pencil, ReceiptText, ScrollText } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape, useAccountingCompany } from "../../../../features/accounting/company.js";
import { GeneralTab } from "../../../../features/counterparties/detail/general-tab.js";
import { InvoicesTab } from "../../../../features/counterparties/detail/invoices-tab.js";
import { LettersTab } from "../../../../features/counterparties/detail/letters-tab.js";
import { NotesTab } from "../../../../features/counterparties/detail/notes-tab.js";
import { OpenItemsTab } from "../../../../features/counterparties/detail/open-items-tab.js";
import { TransactionsTab } from "../../../../features/counterparties/detail/transactions-tab.js";
import { useCounterpartyCard } from "../../../../features/counterparties/detail/use-card.js";
import { CounterpartyPage, RoleBadges, useListCrumb } from "../../../../features/counterparties/shared.js";
import { errorMessage } from "../../../../lib/errors.js";
import { usePermissions } from "../../../../lib/session.js";

const TABS = ["general", "transactions", "open-items", "cheques", "invoices", "letters", "notes"] as const;
type TabKey = (typeof TABS)[number];

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/cari/$id/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CounterpartyDetailPage,
});

function CounterpartyDetailPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const listCrumb = useListCrumb();
  const permissions = usePermissions();
  const card = useCounterpartyCard(id);
  const title = card.data ? `${card.data.code} · ${card.data.name}` : t("menu.counterparties");

  return (
    <CounterpartyPage
      title={title}
      breadcrumb={[listCrumb, { label: card.data?.code ?? "…" }]}
      actions={
        card.data ? (
          <>
            {permissions.has("counterparty.txn.create") && card.data.isActive ? (
              <>
                <Button variant="outline" asChild>
                  <Link to="/cari/hareket-yeni" search={{ counterpartyId: id, type: "COLLECTION" }}>
                    <HandCoins aria-hidden="true" />
                    {t("counterparties.detail.collect")}
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/cari/hareket-yeni" search={{ counterpartyId: id, type: "PAYMENT" }}>
                    <Landmark aria-hidden="true" />
                    {t("counterparties.detail.pay")}
                  </Link>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/cari/hareket-yeni" search={{ counterpartyId: id, type: "DEBIT_NOTE" }}>
                    <ReceiptText aria-hidden="true" />
                    {t("counterparties.detail.otherTransaction")}
                  </Link>
                </Button>
              </>
            ) : null}
            {permissions.has("counterparty.report.read") ? (
              <Button variant="outline" asChild>
                <Link to="/cari/raporlar" search={{ tab: "statement", counterparty: id }}>
                  <ScrollText aria-hidden="true" />
                  {t("counterparties.detail.statement")}
                </Link>
              </Button>
            ) : null}
            {permissions.has("counterparty.card.manage") ? (
              <Button asChild>
                <Link to="/cari/$id/duzenle" params={{ id }}>
                  <Pencil aria-hidden="true" />
                  {t("common.edit")}
                </Link>
              </Button>
            ) : null}
          </>
        ) : null
      }
    >
      {({ companyId }) =>
        card.error ? (
          <Alert tone="danger">{errorMessage(card.error)}</Alert>
        ) : !card.data ? (
          <SkeletonRows rows={8} />
        ) : (
          <CardTabs card={card.data} companyId={companyId} />
        )
      }
    </CounterpartyPage>
  );
}

function CardTabs({ card, companyId }: { card: Counterparty; companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const { companies } = useAccountingCompany();
  const tab: TabKey = Route.useSearch().tab ?? "general";
  const linked = card.companies.some((link) => link.companyId === companyId);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <RoleBadges roles={card.roles} />
        {card.isBlocked ? (
          <StatusBadge tone="danger">{t("counterparties.badges.blocked")}</StatusBadge>
        ) : null}
        {card.isActive ? null : <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>}
      </div>
      {linked ? null : <Alert tone="warning">{t("counterparties.detail.notLinked")}</Alert>}
      <Tabs
        value={tab}
        onValueChange={(next) =>
          void navigate({
            search: (previous) => ({ company: previous.company, tab: next as TabKey }),
            replace: true,
          })
        }
      >
        <TabsList aria-label={t("counterparties.detail.tabsLabel")} className="flex-wrap">
          {TABS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`counterparties.tabs.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="general">
          {tab === "general" ? <GeneralTab card={card} companyId={companyId} companies={companies} /> : null}
        </TabsContent>
        <TabsContent value="transactions">
          {tab === "transactions" ? <TransactionsTab counterpartyId={card.id} companyId={companyId} /> : null}
        </TabsContent>
        <TabsContent value="open-items">
          {tab === "open-items" ? <OpenItemsTab counterpartyId={card.id} companyId={companyId} /> : null}
        </TabsContent>
        {/* Çek ve fatura modülleri henüz yok: sahte veri gösterilmez, yalnızca ne zaman geleceği söylenir. */}
        <TabsContent value="cheques">
          <EmptyState
            headingLevel={2}
            icon={<Landmark />}
            title={t("counterparties.placeholders.chequesTitle")}
            description={t("counterparties.placeholders.chequesDescription")}
          />
        </TabsContent>
        <TabsContent value="invoices">
          {tab === "invoices" ? <InvoicesTab counterpartyId={card.id} companyId={companyId} /> : null}
        </TabsContent>
        <TabsContent value="letters">
          {tab === "letters" ? <LettersTab counterpartyId={card.id} companyId={companyId} /> : null}
        </TabsContent>
        <TabsContent value="notes">
          {tab === "notes" ? <NotesTab counterpartyId={card.id} /> : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
